import {
  ALL_CATALOG_KEYS,
  PLAN_PRODUCTS,
  SELF_SERVICE_PRICE_SPECS,
  requiredPriceEnvVar,
  type BillingCurrency,
  type CanonicalPriceSpec as StripePriceCatalogSpec,
  type PlanProductKey,
  type PriceCatalogKey,
} from '@/lib/billing/catalog';
import type { PlanTier } from '@/lib/planLimits';

export type { BillingCurrency, PlanProductKey, PriceCatalogKey, StripePriceCatalogSpec };
export { ALL_CATALOG_KEYS, PLAN_PRODUCTS, requiredPriceEnvVar };

const PLAN_ALIASES: Record<string, PlanProductKey> = {
  verified: 'individual_verified_monthly',
  pro: 'individual_verified_monthly',
  legacy: 'individual_legacy_monthly',
  collective_verified: 'collective_verified_monthly',
  collective_pro: 'collective_pro_monthly',
  vaas: 'vaas_one_time',
};

export function envVarCandidates(product: PlanProductKey, currency: BillingCurrency): string[] {
  const suffix = product.toUpperCase();
  return [
    requiredPriceEnvVar(product, currency),
    ...(currency === 'usd' ? [`STRIPE_PRICE_${suffix}`] : []),
  ];
}

export function normalizeBillingCurrency(value: unknown): BillingCurrency | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'usd' || normalized === 'us' || normalized === '$') return 'usd';
  if (normalized === 'cad' || normalized === 'ca' || normalized === 'c$') return 'cad';
  return null;
}

export function inferCurrencyFromCountry(countryCode: string | null | undefined): BillingCurrency {
  return (countryCode || '').trim().toUpperCase() === 'CA' ? 'cad' : 'usd';
}

function isKnownProductKey(value: string): value is PlanProductKey {
  return PLAN_PRODUCTS.includes(value as PlanProductKey);
}

export function normalizePlanProductKey(planId: string): PlanProductKey | null {
  let raw = (planId || '').trim().toLowerCase();
  if (!raw) return null;
  raw = raw.replace(/_(usd|cad)$/i, '');
  const normalized = PLAN_ALIASES[raw] || raw;
  return isKnownProductKey(normalized) ? normalized : null;
}

export function buildCatalogKey(
  product: PlanProductKey,
  currency: BillingCurrency
): PriceCatalogKey {
  return `${product}_${currency}`;
}

export const STRIPE_PRICE_CATALOG: readonly StripePriceCatalogSpec[] = SELF_SERVICE_PRICE_SPECS;

export function getStripePriceCatalogSpec(
  catalogKey: PriceCatalogKey
): StripePriceCatalogSpec | null {
  return STRIPE_PRICE_CATALOG.find((entry) => entry.catalogKey === catalogKey) || null;
}

export function parseCatalogKey(catalogKey: string): {
  product: PlanProductKey;
  currency: BillingCurrency;
} | null {
  const match = catalogKey.match(/^(.+)_(usd|cad)$/i);
  if (!match) return null;
  const product = match[1].toLowerCase();
  if (!isKnownProductKey(product)) return null;
  return { product, currency: match[2].toLowerCase() as BillingCurrency };
}

export function resolveCatalogKey(
  planId: string,
  currency: BillingCurrency
): PriceCatalogKey | null {
  const product = normalizePlanProductKey(planId);
  return product ? buildCatalogKey(product, currency) : null;
}

export type ResolvedStripePrice = {
  priceId: string;
  productId: string;
  catalogKey: PriceCatalogKey;
  product: PlanProductKey;
  currency: BillingCurrency;
  envVarUsed: string;
  productEnvVarUsed: string;
};

export type ResolveStripePriceResult =
  | { ok: true; price: ResolvedStripePrice }
  | { ok: false; error: string; catalogKey: PriceCatalogKey | null };

function validStripeId(value: string, prefix: 'price_' | 'prod_'): boolean {
  return value.startsWith(prefix) && /^[A-Za-z0-9_]+$/.test(value);
}

/** Resolve only server-managed identifiers. There are deliberately no hardcoded Price fallbacks. */
export function resolveStripePriceId(
  planId: string,
  currency: BillingCurrency
): ResolveStripePriceResult {
  const product = normalizePlanProductKey(planId);
  if (!product)
    return { ok: false, error: `Unknown self-service plan: ${planId}`, catalogKey: null };

  const catalogKey = buildCatalogKey(product, currency);
  const spec = getStripePriceCatalogSpec(catalogKey);
  if (!spec) return { ok: false, error: `Unknown catalog key: ${catalogKey}`, catalogKey };

  let priceId = '';
  let envVarUsed = spec.requiredEnvVar;
  for (const envName of envVarCandidates(product, currency)) {
    const candidate = (process.env[envName] || '').trim();
    if (candidate) {
      priceId = candidate;
      envVarUsed = envName;
      break;
    }
  }
  if (!priceId) {
    return {
      ok: false,
      error: `No Stripe price configured for ${catalogKey}. Set ${spec.requiredEnvVar}.`,
      catalogKey,
    };
  }
  if (!validStripeId(priceId, 'price_')) {
    return {
      ok: false,
      error: `${envVarUsed} must contain a Stripe Price ID beginning with price_.`,
      catalogKey,
    };
  }

  const productId = (process.env[spec.requiredProductEnvVar] || '').trim();
  if (!productId) {
    return {
      ok: false,
      error: `No Stripe product configured for ${catalogKey}. Set ${spec.requiredProductEnvVar}.`,
      catalogKey,
    };
  }
  if (!validStripeId(productId, 'prod_')) {
    return {
      ok: false,
      error: `${spec.requiredProductEnvVar} must contain a Stripe Product ID beginning with prod_.`,
      catalogKey,
    };
  }

  return {
    ok: true,
    price: {
      priceId,
      productId,
      catalogKey,
      product,
      currency,
      envVarUsed,
      productEnvVarUsed: spec.requiredProductEnvVar,
    },
  };
}

export function listCatalogPriceMappings(): Array<{
  catalogKey: PriceCatalogKey;
  priceId: string | null;
  productId: string | null;
  envVar: string;
  productEnvVar: string;
  requiredEnvVar: string;
  configured: boolean;
}> {
  return STRIPE_PRICE_CATALOG.map((spec) => {
    const resolved = resolveStripePriceId(spec.product, spec.currency);
    if (resolved.ok) {
      return {
        catalogKey: spec.catalogKey,
        priceId: resolved.price.priceId,
        productId: resolved.price.productId,
        envVar: resolved.price.envVarUsed,
        productEnvVar: resolved.price.productEnvVarUsed,
        requiredEnvVar: spec.requiredEnvVar,
        configured: true,
      };
    }
    return {
      catalogKey: spec.catalogKey,
      priceId: null,
      productId: null,
      envVar: spec.requiredEnvVar,
      productEnvVar: spec.requiredProductEnvVar,
      requiredEnvVar: spec.requiredEnvVar,
      configured: false,
    };
  });
}

export function isSubscriptionProduct(product: PlanProductKey): boolean {
  return product !== 'vaas_one_time';
}

export const PLAN_TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  collective_free: 1,
  verified: 10,
  collective_verified: 12,
  institution_starter: 20,
  vaas: 25,
  legacy: 30,
  collective_pro: 32,
  institution_growth: 40,
};
